import type { ReactNode } from 'react'
import {
  BillingItem,
  BillingItemBadge,
  BillingItemContent,
  BillingItemExtras,
  BillingItemList,
  BillingItemSubtitle,
  BillingItemTitle,
  BillingItemVisual,
} from '@/features/billing/components/BillingItemList'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/Button'
import {
  Dropdown,
  DropdownItem,
  DropdownItemDescription,
  DropdownItemTitle,
  DropdownTitle,
} from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { IconVisual } from '@/components/IconVisual'
import { Spinner } from '@/components/Spinner'
import { match } from '@/utils/pattern-match-util'
import { isEmptyNode } from '@/utils/react-util'
import { type BillingMethodDisplayData } from '@/features/billing/payment-methods/billing-method-display'
import styles from './BillingMethodList.module.css'

export interface BillingMethodListProps {
  children?: ReactNode
  onAdd?: () => unknown
  isAddDisabled?: boolean
}

export function BillingMethodList({
  children,
  onAdd,
  isAddDisabled,
}: BillingMethodListProps) {
  return (
    <>
      <BillingItemList>
        {children}
        {onAdd && (
          <Button
            label="Add New Payment Method"
            isDisabled={isAddDisabled}
            block
            variant="text"
            onPress={onAdd}
            testId="add-new-payment-method-button"
          />
        )}
      </BillingItemList>
    </>
  )
}

export interface BillingMethodListItemProps {
  billingMethod: BillingMethodDisplayData
  selected?: boolean
  onPress?: () => unknown
  onDelete?: () => unknown
  isDisabled?: boolean
  isPending?: boolean
  readOnly?: boolean
  dropdownActions?: ReactNode
  testId?: string
}

export function BillingMethodListItem({
  billingMethod,
  selected,
  onPress,
  onDelete,
  isDisabled,
  isPending,
  readOnly,
  dropdownActions,
  testId,
}: BillingMethodListItemProps) {
  return (
    <BillingItem
      onPress={onPress}
      isDisabled={isDisabled}
      isCurrent={selected}
      testId={testId}
      actions={
        !readOnly && (
          <BillingMethodItemActions
            selected={!!selected}
            isPending={!!isPending}
            isDisabled={!!isDisabled}
            billingMethod={billingMethod}
            onDelete={onDelete}
          >
            {dropdownActions}
          </BillingMethodItemActions>
        )
      }
    >
      <BillingMethodListItemDisplay billingMethod={billingMethod} />
      {!readOnly && (
        <BillingItemExtras>
          {selected && <BillingItemBadge>Current</BillingItemBadge>}
          {isPending && <Spinner />}
        </BillingItemExtras>
      )}
    </BillingItem>
  )
}

interface BillingMethodItemActionsProps {
  isPending: boolean
  billingMethod: BillingMethodDisplayData
  onDelete?: () => unknown
  selected: boolean
  isDisabled: boolean
  children?: ReactNode
}

function BillingMethodItemActions({
  isPending,
  billingMethod,
  onDelete,
  children,
  selected,
  isDisabled,
}: BillingMethodItemActionsProps) {
  if (isPending) {
    return null
  }

  const items = (
    <>
      {children}
      {onDelete && (
        <DropdownItem
          onPress={onDelete}
          icon={<Icon name="delete" />}
          isDisabled={selected}
        >
          <DropdownItemTitle>Delete</DropdownItemTitle>
          <DropdownItemDescription>
            {billingMethod.isDefault || selected ? (
              <>You can't delete the selected payment method.</>
            ) : (
              <>Remove this payment method.</>
            )}
          </DropdownItemDescription>
        </DropdownItem>
      )}
    </>
  )

  if (isEmptyNode(items)) {
    return null
  }

  return (
    <Dropdown
      autoDismiss
      isDisabled={isDisabled}
      position="bottom-end"
      button={<IconButton name="more" label="Billing method actions" />}
    >
      <DropdownTitle>Actions</DropdownTitle>
      {items}
    </Dropdown>
  )
}

interface BillingMethodListItemDisplayProps {
  billingMethod: BillingMethodDisplayData
}

function BillingMethodListItemDisplay({
  billingMethod,
}: BillingMethodListItemDisplayProps) {
  return match(billingMethod.variant, {
    credit: () => (
      <>
        <BillingItemVisual>
          <Avatar
            size={48}
            subject={{
              avatar: billingMethod.workspaceIcon,
              displayName: '',
            }}
          />
        </BillingItemVisual>
        <BillingItemContent>
          <BillingItemTitle testId="billing-method-title">
            {billingMethod.title}
          </BillingItemTitle>
          <BillingItemSubtitle testId="billing-method-subtitle">
            Statement Balance &nbsp;&nbsp;{' '}
            <Balance balance={billingMethod.balance} />
          </BillingItemSubtitle>
          <BillingItemSubtitle>
            Remaining Balance &nbsp;&nbsp;{' '}
            <Balance isRemaining balance={billingMethod.remainingBalance} />
          </BillingItemSubtitle>
        </BillingItemContent>
      </>
    ),
    card: () => (
      <>
        <BillingItemVisual>
          <Icon size={48} name={billingMethod.cardIcon} />
        </BillingItemVisual>
        <BillingItemContent>
          <BillingItemTitle testId="billing-method-title">
            {billingMethod.title}
          </BillingItemTitle>
          <BillingItemSubtitle testId="billing-method-subtitle">
            Card no:{' '}
            <span className={styles.mono}>
              **** **** **** {billingMethod.last4}
            </span>
          </BillingItemSubtitle>
          <BillingItemSubtitle>
            Expires: {billingMethod.expMonth}/{billingMethod.expYear}
          </BillingItemSubtitle>
        </BillingItemContent>
      </>
    ),
    unknown: () => (
      <>
        <BillingItemVisual>
          <IconVisual>
            <Icon name="info" />
          </IconVisual>
        </BillingItemVisual>
        <BillingItemContent>
          <BillingItemTitle testId="billing-method-title">
            {billingMethod.title}
          </BillingItemTitle>
          <BillingItemSubtitle>
            This version of the app does not recognize this payment type.
          </BillingItemSubtitle>
        </BillingItemContent>
      </>
    ),
  })
}

interface BalanceProps {
  balance: number
  isRemaining?: boolean
}

function Balance({ balance, isRemaining }: BalanceProps) {
  return (
    <span
      className={
        isRemaining
          ? styles.remaining
          : balance > 0
            ? styles.positiveBalance
            : styles.negativeBalance
      }
    >
      {moneyFormatter.format(balance / 100)}
    </span>
  )
}

const moneyFormatter = new Intl.NumberFormat('en-US', {
  currency: 'usd',
  style: 'currency',
})
